import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { ReportSelectComponent } from '../../../../shared/component/report-select/report-select.component';
import { TableComponent } from '../../../../shared/component/table/table.component';
import { DestroyableComponent } from '../../../../shared/services/destroyable.component';

import {
  ReportPaginationService,
  ReportPayload
} from '../report-pagination.service';

export interface ReportConfig {
  header: string;
  reportType: string;
  downloadHeader?: string;
  apiUrl?: string;
  reports?: boolean;
  consumer?: boolean;
  Date_Flat?: boolean;
  AllSelection?: boolean;
  showDaterange?: boolean;
  showSingleDate?: boolean;
  Month_Year?: boolean;
  Date_Time?: boolean;
  SiteOnly?: boolean;
  showLocation?: boolean;
  SingleLocationDropdown?: boolean;
  ShowLocationDropdown?: boolean;
  showSites?: boolean;
  showsubtype?: boolean;
  showBillNo?: boolean;
  Hours?: boolean;
  filterConfig?: any[];
  orderBy?: string;
  location?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-unified-report',
  standalone: true,
  imports: [TableComponent, CommonModule, ReportSelectComponent],
  templateUrl: './unified-report.component.html',
  styleUrl: './unified-report.component.scss'
})
export class UnifiedReportComponent extends DestroyableComponent implements OnInit, OnDestroy {
  showtable = false;
  totalCount = 0;
  configLoaded = false; // guards app-report-select from rendering before JSON is fetched
  loginType: string | null = localStorage.getItem('login_Type');

  // Default configuration if not provided via route
  config: ReportConfig = {
    header: 'Unified Report',
    reportType: 'DYNAMIC_REPORT',

  };

  constructor(
    private reportService: ReportPaginationService,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    super();
  }

  ngOnInit(): void {
    this.reportService.reset();
    this.route.url.pipe(takeUntil(this.destroy$)).subscribe((segments) => {
      // Derive config key from the last segment of the current route path
      // e.g. reports/recharge_report  →  "recharge_report"
      const reportId = segments.length > 0 ? segments[segments.length - 1].path : null;
      if (reportId) {
        this.http.get<any>('assets/report-configs.json').subscribe({
          next: (configs) => {
            if (configs && configs[reportId]) {
              this.config = { ...this.config, ...configs[reportId] };

              // Filter out Facility Admin option if user is not AA or DEV
              if (this.config.filterConfig && Array.isArray(this.config.filterConfig)) {
                const isSuperUser = this.loginType === 'AA' || this.loginType === 'DEV';
                this.config.filterConfig = this.config.filterConfig.map((field: any) => {
                  if (field.apiKey === 'type' && field.options) {
                    const filteredOptions = field.options.filter((opt: any) => {
                      const val = opt.value ?? opt.id;
                      if (val === 'FA' && !isSuperUser) {
                        return false;
                      }
                      return true;
                    });
                    return { ...field, options: filteredOptions };
                  }
                  return field;
                });
              }
            }
            this.configLoaded = true; // render app-report-select only after config is ready
          },
          error: (err) => {
            console.error('Failed to load report configs', err);
            this.configLoaded = true; // render with defaults on error
          }
        });
      }
    });

    this.reportService.showTable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => (this.showtable = v));

    this.reportService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((c) => (this.totalCount = c));
  }

  // 🔹 Filter submit
  private lastFilters: Partial<ReportPayload> | null = null;

  handleFilters(rawPayload: Partial<ReportPayload>) {
    this.lastFilters = rawPayload;
    const isCO = this.loginType === 'CO';
    const coLocationId = localStorage.getItem('login_id') || '';

    // 🔹 Normalize payload
    const payload: ReportPayload = {
      ...rawPayload,
      from_date: rawPayload.from_date ?? '',
      to_date: rawPayload.to_date ?? '',
      site_ids: rawPayload.site_ids || (isCO ? (sessionStorage.getItem('consumerSiteId') || '') : ''),
      loc_id: isCO ? (coLocationId || rawPayload.loc_id) : rawPayload.loc_id,
      loc_from: rawPayload.loc_from,
      loc_to: rawPayload.loc_to,
      date: rawPayload.date,
      otherType: isCO ? (rawPayload['otherType'] || rawPayload.subtype || '') : (rawPayload['otherType'] ?? ''),
      subtype: rawPayload.subtype,
      state: rawPayload['Status'] || rawPayload['state'] || rawPayload['State'] || '',
      Status: rawPayload['Status'] || rawPayload['state'] || rawPayload['State'] || '',
      orderBy: this.config.orderBy || this.config['OrderBy'] || '',
      OrderBy: this.config.orderBy || this.config['OrderBy'] || ''
    };

    if (rawPayload.loc_id && !isCO) {
      payload.water_loc_id = rawPayload.loc_id;
    } else if (rawPayload.water_loc_id) {
      payload.water_loc_id = rawPayload.water_loc_id;
    }

    this.reportService.initFilters(
      payload,
      this.config.reportType || this.config['apiUrl'] || 'Reports',
      this.destroy$,
      this.config.downloadHeader || this.config.header,
      this.config['apiUrl'] || 'Reports',
      this.config
    );
  }

  onColumnFormSubmitted(event: any): void {
    if (this.lastFilters) {
      this.handleFilters(this.lastFilters);
    }
  }

  // 🔹 Pagination
  handlePageChange(event: { page: number; pageSize: number }) {
    this.reportService.changePage(event, this.destroy$);
  }

  // 🔹 Reset
  override ngOnDestroy(): void {
    this.handlereset(false);
  }

  handlereset(show: boolean) {
    if (!show) {
      this.reportService.reset();
    }
  }
}
